import { api, type Api } from "@api/bot-base-api-request";
import { useMutation } from "@tanstack/react-query";

/**
 * Mutation key for pausing all copy trades
 */
export const BOT_PAUSE_ALL_COPY_TRADE_QUERY_KEY = "bot-pause-all-copy-trade";

/**
 * Parameters for pausing all copy trades
 * @typedef {Object} BotPauseAllCopyTradeParams
 * @property {string} userId - User's unique identifier
 * @property {string} walletAddress - User's wallet address
 * @property {number} chainId - ID of the blockchain network
 */
export type BotPauseAllCopyTradeParams = {
  userId: string;
  walletAddress: string;
  chainId: number;
  appPubKey?: any;
};

/**
 * Response structure for pausing all copy trades
 * @typedef {Object} UpdateCopyTradeResponse
 * @property {string} message - Success or error message
 * @property {Array<Object>} copyTradeData - Updated array of wallets being copied
 */
export type UpdateCopyTradeResponse = {
  message: string;
  copyTradeData: Array<{
    address: string;
    nickname?: string;
    amountPercent?: number;
    isDuplicateBuy?: boolean;
    slippage?: number;
    tokenRenounced?: boolean;
    tokenMinLiquidity?: number;
    tokenMaxLiquidity?: number;
    tokenMinMC?: number;
    tokenMaxMC?: number;
    tokenMaxBuyTax?: number;
    tokenMaxSellTax?: number;
    copySell?: boolean;
    isOn?: boolean;
    useProfile?: boolean;
    createAt?: number;
    isBuyWithFixedAmount?: boolean;
    amount?: number;
    isAutoSlippage?: boolean;
  }>;
};

/**
 * Pauses all copy trades for a user wallet
 *
 * @param {Api} api - API instance
 * @param {BotPauseAllCopyTradeParams} params - Parameters for the API request
 * @returns {Promise<UpdateCopyTradeResponse>} Promise that resolves to update response
 */
export const postBotPauseAllCopyTrade = async (
  api: Api,
  params: BotPauseAllCopyTradeParams
): Promise<UpdateCopyTradeResponse> =>
  api.post("copyTrade/pauseAll", { json: params }).json();

/**
 * React Query mutation hook for pausing all copy trades
 *
 * @returns {UseMutationResult<UpdateCopyTradeResponse, Error, BotPauseAllCopyTradeParams>} React Query mutation result
 *
 * @example
 * // Use mutation to pause all copy trades
 * const pauseAllMutation = useBotPauseAllCopyTradeMutation();
 *
 * // Later in your code:
 * pauseAllMutation.mutate({
 *   userId: "user123",
 *   walletAddress: "0x123...abc",
 *   chainId: 1
 * });
 */
export const useBotPauseAllCopyTradeMutation = () =>
  useMutation({
    mutationFn: async (params: BotPauseAllCopyTradeParams) =>
      postBotPauseAllCopyTrade(api, params),
    mutationKey: [BOT_PAUSE_ALL_COPY_TRADE_QUERY_KEY],
  });
